import React from 'react'
import { useMutation, useQueryClient } from 'react-query'
import { useToast } from '@chakra-ui/react'
import { delayFn, getAppConfigSetting } from '../utils'
import { SENTNEL2_UI } from './constants'
import queryKeys from './queryKeys'
import axios, { AxiosError } from 'axios'
import { Alert, useAlert } from '@apex/component-library'
// TODO: Replace with ui-components toast

export const buildApiUrl = (id: string) => {
  const getBaseUrl = () => `${getAppConfigSetting('apiUrl')}${SENTNEL2_UI}`
  return `${getBaseUrl()}/evaluated_transfers/${id}/cancel`
}

export const cancelTransfer = (transferId: string) => async (payload: {}) => {
  const { data } = await axios.put(buildApiUrl(transferId), payload, {
    withCredentials: true
  })
  return data
}

interface Options {
  onMutate?: Function
  onError?: Function
  onSuccess?: Function
  onSettled?: Function
}

export function useCancelTransfer(transferId: string, accountNumber: string, options?: Options) {
  const { onError, onSuccess, onSettled } = options || {}
  const queryClient = useQueryClient()
  const toast = useToast()
  const { setAlert } = useAlert()
  return useMutation(cancelTransfer(transferId), {
    onError: (error: AxiosError, variables, context) => {
      if (error.response?.status && error.response.status >= 400 && error.response.status < 500) {
        toast({
          position: 'top-right',
          variant: 'left-accent',
          isClosable: true,
          title: 'The request failed.',
          description: `${error.response.data.errorDescription}`,
          status: 'error',
          duration: null,
          render: ({ onClose, id }) => (
            <Alert
              sx={{
                width: 340,
                color: 'black',
                bg: 'white',
                boxShadow: '0px 0px 5px 3px rgba(153,153,153,0.5)'
              }}
              direction='column'
              variant='left-accent'
              title='The request failed.'
              description={`${error?.response?.data.errorDescription}`}
              status='error'
              isClosable
              dismiss={onClose}
              id={id as string}
            />
          )
        })
      } else {
        setAlert({
          direction: 'row',
          variant: 'left-accent',
          title: 'System Unavailable',
          description: 'Sorry, this service is temporarily unavailable. The request failed.',
          status: 'error',
          isClosable: true
        })
      }
      onError && onError()
    },
    onSuccess: async () => {
      const cashTransfersTableArray: any[] | undefined = queryClient.getQueryData([queryKeys.transfers, accountNumber])
      const canceledEntryInCashTransferTableArray = cashTransfersTableArray!.find(
        (i: { id: string }) => i.id === transferId
      )
      if (!(canceledEntryInCashTransferTableArray === undefined)) {
        queryClient.invalidateQueries([queryKeys.transfers])
      } else {
        delayFn(() => queryClient.invalidateQueries(queryKeys.positionJournalTransfers), 2000)
      }

      toast({
        duration: 8000,
        position: 'top-right',
        variant: 'left-accent',
        status: 'success',
        isClosable: true,
        render: ({ onClose, id }) => (
          <Alert
            sx={{
              width: 340,
              color: 'black',
              bg: 'white',
              boxShadow: '0px 0px 5px 3px rgba(153,153,153,0.5)'
            }}
            direction='column'
            variant='left-accent'
            title='Success!'
            description='The transfer has been canceled.'
            status='success'
            isClosable
            dismiss={onClose}
            id={id as string}
          />
        )
      })
      onSuccess && onSuccess()
    },
    onSettled: (data, error, variables, context) => {
      // do stuff after Error or success.
      onSettled && onSettled()
    }
  })
}
